import React, { useState } from 'react';
import { Paperclip, Upload, Download, X, FileUp } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { useToast } from '../../core/shell/ToastProvider';

/** Datei als data-URL (base64) lesen — der Inhalt MUSS mit ins Attachment-Objekt:
 *  rohe File-Objekte serialisieren nicht (JSON.stringify(File) = {}), es käme also nie
 *  Inhalt im Backend an. commitService/extractBase64 nimmt data-URLs direkt (§7.4 P18).
 *  `file` bleibt zusätzlich für den Sofort-Download in derselben Session erhalten. */
const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('read failed'));
  reader.readAsDataURL(file);
});

// Nur echte base64-data-URLs als Download-Ziel zulassen — attachments können über
// "Erneut anlegen" aus fremden BANFs stammen; ohne Prüfung wäre href beliebiger String.
const DATA_URL_RE = /^data:[\w.+-]+\/[\w.+-]+;base64,/i;

const AttachmentsTab = ({ item, onUpdate }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const { showError } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [isReading, setIsReading] = useState(false);

  const attachments = Array.isArray(item.attachments) ? item.attachments : [];

  // Handle file processing
  const handleFiles = async (files) => {
    if (isReading) return; // kein Doppelstart, solange der FileReader läuft
    const maxSize = 10 * 1024 * 1024; // 10MB — Kette Cart -> Commit -> Adapter -> SBX ist darauf ausgelegt
    const all = Array.from(files);
    all.filter(file => file.size > maxSize).forEach(file => {
      showError?.(t('banf.attachments.tooLarge', { name: file.name }));
    });
    const validFiles = all.filter(file => file.size <= maxSize);
    if (validFiles.length === 0) return;

    setIsReading(true);
    const newAttachments = [];
    for (const file of validFiles) {
      try {
        const data = await readAsDataUrl(file);
        newAttachments.push({
          id: Date.now() + Math.random() * 1000,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          data, // data-URL mit base64-Inhalt — DAS wird nach SAP übertragen
        });
      } catch {
        showError?.(t('banf.attachments.readError', { name: file.name }));
      }
    }
    setIsReading(false);
    if (newAttachments.length > 0) {
      onUpdate(item.id, 'attachments', [...attachments, ...newAttachments]);
    }
  };

  // Drag event handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
          <Paperclip className="w-4 h-4 mr-1.5" />
          {t('banf.attachments.upload')}
        </label>

        {/* Drag & Drop Zone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            relative mb-4 p-8 border-2 border-dashed rounded-2xl transition-all duration-300
            ${isDragging
              ? isDarkMode
                ? 'border-green-400 bg-green-900/20 scale-[1.02]'
                : 'border-brand bg-brand/5 scale-[1.02]'
              : isDarkMode
                ? 'border-gray-600 bg-gray-800/20 hover:border-gray-500'
                : 'border-slate-300 bg-slate-50 hover:border-slate-400'
            }
          `}
        >
          <div className="flex flex-col items-center justify-center">
            <FileUp className={`
              w-12 h-12 mb-4 transition-all duration-300
              ${isDragging
                ? isDarkMode ? 'text-green-500 scale-110 animate-bounce' : 'text-brand scale-110 animate-bounce'
                : isDarkMode
                  ? 'text-gray-400'
                  : 'text-slate-400'
              }
            `} />

            <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
              {isDragging ? t('banf.attachments.dropHere') : t('banf.attachments.dragHere')}
            </p>

            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              {t('common.or')}
            </p>

            {/* Upload Button */}
            <div>
              <input
                type="file"
                multiple
                id={`file-upload-${item.id}`}
                className="hidden"
                disabled={isReading}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    handleFiles(files);
                  }
                  // Reset, damit dieselbe Datei nach Entfernen erneut wählbar ist
                  e.target.value = '';
                }}
              />
              <label
                htmlFor={`file-upload-${item.id}`}
                className={`relative group/upload overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] inline-block ${isReading ? 'opacity-60 pointer-events-none cursor-wait' : 'cursor-pointer'} ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8]'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.6) 0%, rgba(16,185,129,0.6) 50%, rgba(5,150,105,0.6) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                } : undefined}
              >
                <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300">
                  <Upload className="w-4 h-4 mr-2" />
                  {isReading ? t('banf.attachments.reading') : t('banf.attachments.selectFiles')}
                </div>
              </label>
            </div>

            <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>
              {t('banf.attachments.maxSize')}
            </p>
          </div>

          {/* Drag overlay effect */}
          {isDragging && (
            <div className="absolute inset-0 rounded-2xl pointer-events-none">
              <div className={`
                absolute inset-0 rounded-2xl animate-pulse
                ${isDarkMode
                  ? 'bg-gradient-to-r from-green-500/10 via-green-400/10 to-green-500/10'
                  : 'bg-brand/5'
                }
              `} />
            </div>
          )}
        </div>

        {/* Attachments List */}
        {attachments.length > 0 && (
          <div className="space-y-3">
            <h4 className={`text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
              {t('banf.attachments.uploaded', { count: attachments.length })}
            </h4>
            {attachments.map((attachment) => {
              const downloadHref = (typeof attachment.data === 'string' && DATA_URL_RE.test(attachment.data))
                ? attachment.data
                : null;
              const canDownload = Boolean(downloadHref || attachment.file);
              const metaOnly = !attachment.data && !attachment.file;
              return (
              <div key={attachment.id} className="relative group/attachment">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/20 border border-white/10 backdrop-blur-xl' : 'bg-white border border-slate-200'} rounded-2xl`}></div>

                <div className="relative p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isDarkMode ? 'bg-blue-500/20' : 'bg-brand/10'}`}>
                      <Paperclip className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-brand'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'} truncate`}>
                        {attachment.name}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        {attachment.size < 1024
                          ? `${attachment.size} B`
                          : attachment.size < 1024 * 1024
                            ? `${(attachment.size / 1024).toFixed(1)} KB`
                            : `${(attachment.size / (1024 * 1024)).toFixed(1)} MB`
                        }
                        {metaOnly && (
                          <span className={isDarkMode ? 'text-amber-300' : 'text-amber-600'}>
                            {' '}· {t('banf.attachments.metaOnly')}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {canDownload && (
                    <button
                      onClick={() => {
                        // Download: base64-data-URL (überlebt Neuladen) oder File-Objekt (nur diese Session)
                        const href = downloadHref
                          || (attachment.file ? URL.createObjectURL(attachment.file) : null);
                        if (!href) return;
                        const a = document.createElement('a');
                        a.href = href;
                        a.download = attachment.name;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        if (!downloadHref) URL.revokeObjectURL(href);
                      }}
                      className={`p-1 rounded-lg ${isDarkMode ? 'hover:bg-gray-700/50 text-gray-400 hover:text-gray-200' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'} transition-colors`}
                      title={t('banf.attachments.download')}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    )}
                    <button
                      onClick={() => {
                        const updatedAttachments = attachments.filter(att => att.id !== attachment.id);
                        onUpdate(item.id, 'attachments', updatedAttachments);
                      }}
                      className="text-red-500 hover:text-red-600 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title={t('banf.attachments.remove')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentsTab;
